"use client";

import {ArrowRight} from "lucide-react";
import Link from "next/link";
import {Typography} from "@repo/ui/aops-components/typography";
import type {DiscoveryPageQueryResult} from "@repo/sanity-utils/schema/types";
import {DiscoveryButton} from "@/app/components/DiscoveryButton";
import {AopsPortableText} from "@/app/components/PortableText/PortableTextComponents";
import {PanelCornerDots} from "@/app/components/PanelCornerDots";
import {sendDiscoveryEventBeacon} from "@/lib/discoveryEventBeacon";
import styles from "./DiscoveryProgramList.module.css";

type DiscoveryPageBlocks = NonNullable<
	NonNullable<DiscoveryPageQueryResult>["pageContent"]
>;

export type DiscoveryProgramListBlock = Extract<
	DiscoveryPageBlocks[number],
	{_type: "discoveryProgramList"}
>;

type DiscoveryProgramListProps = {
	block: DiscoveryProgramListBlock;
};

type ProgramTile = NonNullable<
	NonNullable<DiscoveryProgramListBlock["programs"]>[number]
>;

function ProgramTileCard({program}: {program: ProgramTile}) {
	const name = program.name?.trim();
	if (!name) return null;

	const badge = program.badge?.trim();
	const shortDescription = program.shortDescription;
	const hasShortDescription =
		Array.isArray(shortDescription) && shortDescription.length > 0;
	const slug = program.slug?.trim();
	const content = (
		<>
			<PanelCornerDots
				className={styles.tileDots}
				dotClassName={styles.tileDot}
			/>
			<div className={styles.tileBody}>
				{(badge || name) && (
					<div className={styles.tileTitleGroup}>
						{badge && (
							<Typography variant="eyebrow" as="p" className={styles.tileBadge}>
								{badge}
							</Typography>
						)}
						<Typography as="h3" variant="h4" className={styles.tileName}>
							{name}
						</Typography>
					</div>
				)}
				<div className={styles.tileDescriptionRow}>
					{hasShortDescription && (
						<AopsPortableText
							value={shortDescription}
							className={styles.tileDescription}
						/>
					)}
					<span className={styles.tileArrow} aria-hidden>
						<ArrowRight size={24} strokeWidth={2.4} />
					</span>
				</div>
			</div>
			<PanelCornerDots
				className={styles.tileDots}
				dotClassName={styles.tileDot}
			/>
		</>
	);

	if (slug) {
		return (
			<Link
				href={`/program/${slug}`}
				className={styles.tile}
				onClick={() =>
					sendDiscoveryEventBeacon("aops__discovery__program_selected", {
						program: slug,
						cta_location: "homepage",
					})
				}
			>
				{content}
			</Link>
		);
	}

	return <div className={styles.tile}>{content}</div>;
}

export function DiscoveryProgramList({block}: DiscoveryProgramListProps) {
	const programs = (Array.isArray(block.programs) ? block.programs : []).filter(
		(program): program is ProgramTile =>
			program != null && Boolean(program.name?.trim())
	);
	const buttons = Array.isArray(block.buttons) ? block.buttons : [];
	const footer = block.footer?.trim();
	const hasFooter = Boolean(footer) || buttons.length > 0;

	if (!block.title && programs.length === 0 && !hasFooter) {
		return null;
	}

	return (
		<div className={styles.section} data-discovery-program-list>
			{(block.eyebrow || block.title) && (
				<header className={styles.header}>
					{block.eyebrow && (
						<Typography variant="eyebrow" as="p" className={styles.eyebrow}>
							{block.eyebrow}
						</Typography>
					)}
					{block.title && (
						<Typography as="h2" variant="h2" className={styles.title}>
							{block.title}
						</Typography>
					)}
				</header>
			)}

			{programs.length > 0 && (
				<div className={styles.tiles}>
					{programs.map((program) => (
						<ProgramTileCard
							key={program._id ?? program.slug ?? program.name}
							program={program}
						/>
					))}
				</div>
			)}

			{hasFooter && (
				<div className={styles.footer}>
					{footer && (
						<Typography variant="body" as="p" className={styles.footerText}>
							{footer}
						</Typography>
					)}
					{buttons.length > 0 && (
						<div className={styles.buttons}>
							{buttons.map((button, index) => (
								<DiscoveryButton
									key={button._key ?? `${button.text}-${index}`}
									button={button}
									className={styles.button}
								/>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
